import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEntitlements } from 'State/User';
import { StaticLinkButton } from 'Components/Fields/Buttons';
import { SheerIdFinePrint } from 'Components/SheerIdFinePrint';
import { SHEER_PAGE_TYPES, SHEER_PAGE_ERROR_MESSAGES, SHEER_PAGE_TITLES } from 'Utils/SheerIdHelpers';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { getPlans } from 'State/Plans';
import RestartVerificationButton from '../components/RestartVerificationButton';
import ExitVerificationButton from '../components/ExitVerificationButton';
import CenterFrameLayout from 'Layouts/CenterFrameLayout';
import { useStore } from 'Hooks/useStore';
import './SheerIdStatusPage.css';

export const SheerIdStatusPage = ({
  pageType,
  setPage,
  setTitle,
  setLoading,
  program,
  errorIds = [],
  layoutProps,
  path,
}) => {
  const {
    routes,
    defaultConfig: { rootDomain },
  } = useStore('appConfig');
  const { bango, sheerId } = useStore('integrations');
  const { isVerizonBango, redirectUrl } = bango;
  const { verificationId } = sheerId;
  const { hasSubscription } = useStore('subscription');
  const [savedRedirect, setSavedRedirect] = useState(redirectUrl);
  const [isSuccess, setSuccess] = useState(false);
  const navigate = useNavigate();

  const isErrorPage = pageType === SHEER_PAGE_TYPES.ERROR;

  let errorMessage = SHEER_PAGE_ERROR_MESSAGES.unknownError;

  if (errorIds.length > 0) {
    errorMessage = SHEER_PAGE_ERROR_MESSAGES[errorIds[0]] || errorMessage;
  }

  const text = {
    [SHEER_PAGE_TYPES.SUCCESS]: {
      BODY: savedRedirect
        ? 'Your account has been successfully activated with Verizon +play subscription. Enjoy Fox Nation!'
        : 'Continue to the next step to create your Fox Nation account and checkout with your military discount.',
      PRIMARY_BUTTON: savedRedirect ? 'Continue to Verizon' : 'Continue to FOX Nation',
      PRIMARY_PATH: savedRedirect || path || rootDomain,
    },
    [SHEER_PAGE_TYPES.ERROR]: {
      BODY: errorMessage,
      PRIMARY_BUTTON: savedRedirect ? 'Back to Verizon' : 'Back to FOX Nation',
      PRIMARY_PATH: savedRedirect || rootDomain,
    },
    [SHEER_PAGE_TYPES.INELIGIBLE]: {
      BODY: savedRedirect
        ? 'It looks like you are already a valued Fox Nation subscriber. If you would like to purchase an additional subscription through Verizon +play, please use a new email address.'
        : 'It looks like you are already a valued Fox Nation subscriber. If you would like to purchase an additional subscription, please use a new email address.',
      PRIMARY_BUTTON: savedRedirect ? 'Continue to Verizon' : 'Continue to FOX Nation',
      PRIMARY_PATH: savedRedirect || rootDomain,
    },
  };

  useEffect(() => {
    const activate = () => {
      try {
        getEntitlements();
        setLoading(false);
        setTitle(SHEER_PAGE_TITLES[SHEER_PAGE_TYPES.SUCCESS]);
        trackEvent(TRACKING_ENUMS.EVENT_STATUS_VERIFICATION_COMPLETED, {
          page_partner: isVerizonBango ? 'bango' : 'fox',
          page_program: program,
        });
      } catch (error) {
        setPage(SHEER_PAGE_TYPES.ERROR, { verificationId, ...error });
        setLoading(false);
      }
    };

    if (pageType === SHEER_PAGE_TYPES.SUCCESS && !isSuccess && savedRedirect) {
      // This is to only call activate once on status change
      setSuccess(true);
      activate();
    }
    if (pageType === SHEER_PAGE_TYPES.SUCCESS && !isSuccess && !savedRedirect) {
      // This is nonbango user success page
      const callback = async () => {
        await getPlans();
        trackEvent(TRACKING_ENUMS.EVENT_STATUS_VERIFICATION_COMPLETED, {
          page_partner: isVerizonBango ? 'bango' : 'fox',
          page_program: program,
        });
        setSuccess(true);
        setLoading(false);
      };
      callback();
    }
    if (pageType === SHEER_PAGE_TYPES.ERROR) {
      setTitle(SHEER_PAGE_TITLES[SHEER_PAGE_TYPES.ERROR]);
      trackEvent(TRACKING_ENUMS.EVENT_STATUS_VERIFICATION_REJECTED);
      setLoading(false);
    }

    if (pageType === SHEER_PAGE_TYPES.INELIGIBLE) {
      setTitle(SHEER_PAGE_TITLES[SHEER_PAGE_TYPES.INELIGIBLE]);
      trackEvent(TRACKING_ENUMS.EVENT_STATUS_VERIFICATION_REJECTED);
      setLoading(false);
    }
  }, [
    setLoading,
    savedRedirect,
    pageType,
    setTitle,
    setSavedRedirect,
    setPage,
    verificationId,
    isSuccess,
    navigate,
    path,
    isVerizonBango,
    program,
  ]);

  return (
    <CenterFrameLayout {...layoutProps}>
      <div className="sheerIdStatusPage">
        <div className="sheerIdStatusMainText">{text[pageType].BODY}</div>
        {isErrorPage && <RestartVerificationButton setPage={setPage} isBango={isVerizonBango} />}
        {isErrorPage && (
          <ExitVerificationButton routes={routes} hasSubscription={hasSubscription} isBango={isVerizonBango} />
        )}
        {!!text[pageType].PRIMARY_BUTTON && !!text[pageType].PRIMARY_PATH && (
          <StaticLinkButton
            className={`sheerIdStatusPrimaryButton ${isErrorPage ? 'hasFinePrint' : ''}`}
            path={text[pageType].PRIMARY_PATH}
            text={text[pageType].PRIMARY_BUTTON}
          />
        )}
        {isErrorPage && <SheerIdFinePrint isReview />}
      </div>
    </CenterFrameLayout>
  );
};
